import { Inject, Injectable, InjectionToken, NgZone } from '@angular/core'
import { LocalStorageService } from 'ngx-webstorage'
import { empty, Observable, timer } from 'rxjs'
import { PlatformService } from '../../singleton-services/platform.service'
import { map, takeUntil, takeWhile, tap } from 'rxjs/operators'

export const MAX_VID_PREVIEW_SECS = new InjectionToken<number>('cfg.vid.prev.secs')
export const PREVIEW_STORE_KEY = new InjectionToken<string>('cfg.vid.prev.sto')

export interface IPreviewTrackingModel {
  canPreview: boolean
  secondsRemaining: number
  secondsViewed: number
}

export interface IPreviewService {
  beginTrackingViewTime(videoId: string, cancellationStream: Observable<any>): Observable<IPreviewTrackingModel>
  summary(videoId: string): IPreviewTrackingModel
}

interface NumberDictionary {
  readonly [key: string]: number
}

const TIMER_MS = 1000
const TIMER_SEC = TIMER_MS / 1000

const timeLeft = (secondsViewed: number, maxPreviewInSeconds: number) => maxPreviewInSeconds - secondsViewed
const canPreviewVideo = (secondsViewed: number, maxPreviewInSeconds: number) => secondsViewed < maxPreviewInSeconds

@Injectable()
export class PreviewService implements IPreviewService {
  constructor(
    @Inject(MAX_VID_PREVIEW_SECS) private maxPreviewSeconds: number,
    @Inject(PREVIEW_STORE_KEY) private previewStoKey: string,
    private lss: LocalStorageService,
    private ps: PlatformService,
    private zone: NgZone
  ) {}

  readonly beginTrackingViewTime = (videoId: string, cancellationStream: Observable<any>) =>
    this.ps.isServer
      ? empty()
      : this.zone.runOutsideAngular(() =>
          timer(TIMER_MS, TIMER_MS).pipe(
            map<number, IPreviewTrackingModel>(() => this.summary(videoId)),
            tap(() => this.tick(videoId)),
            takeWhile(a => a.secondsRemaining >= 0),
            takeUntil(cancellationStream)
          )
        )

  readonly summary = (videoId: string) => {
    const secondsViewed = this.getVideoSecondsPreviewed(videoId)
    const secondsRemaining = timeLeft(secondsViewed, this.maxPreviewSeconds)
    const canPreview = canPreviewVideo(secondsViewed, this.maxPreviewSeconds)

    return {
      videoId,
      secondsViewed,
      canPreview,
      secondsRemaining
    } as IPreviewTrackingModel
  }

  private readonly getStore = () => (this.lss.retrieve(this.previewStoKey) || {}) as NumberDictionary
  private readonly setStore = (store: NumberDictionary) => this.lss.store(this.previewStoKey, store)
  private readonly getVideoSecondsPreviewed = (videoId: string) => this.getStore()[videoId] || 0
  private readonly tick = (videoId: string, incr = TIMER_SEC) =>
    this.setVideoInStore(videoId, this.getVideoSecondsPreviewed(videoId) + incr)

  private setVideoInStore = (videoId: string, seconds: number) =>
    this.setStore({
      ...this.getStore(),
      [videoId]: seconds
    })
}
